import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Tag, ShoppingCart, BadgePercent } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { outletProducts } from '../data/mock';
import PurchaseModal from './PurchaseModal';

const OutletSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = ['Todos', ...new Set(outletProducts.map(p => p.category))];
  
  const filteredProducts = selectedCategory === 'Todos' 
    ? outletProducts 
    : outletProducts.filter(p => p.category === selectedCategory);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      maximumFractionDigits: 0
    }).format(price);
  };

  const calculateDiscount = (original, discount) => {
    return Math.round(((original - discount) / original) * 100);
  };

  const handleBuyProduct = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  return (
    <section className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600/20 border border-violet-500/30 rounded-full mb-6"
          >
            <BadgePercent className="w-4 h-4 text-violet-400" />
            <span className="text-violet-300 text-sm font-mono tracking-wider uppercase">Ofertas Especiales</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Outlet
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Equipos reacondicionados y nuevos con garantía incluida
          </motion.p>
        </div>

        {/* Category Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-10"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-mono tracking-wide transition-all
                ${selectedCategory === category 
                  ? 'bg-violet-600 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-violet-900/30'
                }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Products Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredProducts.map((product) => (
            <motion.div 
              key={product.id} 
              variants={itemVariants}
              whileHover={{ y: -4 }}
              className="cursor-pointer"
              onClick={() => handleBuyProduct(product)}
            >
              <Card className="bg-white/5 border-violet-900/30 hover:border-violet-500/50 transition-all h-full flex flex-col hover:shadow-lg hover:shadow-violet-500/10 overflow-hidden">
                {/* Product Image */}
                <div className="relative aspect-square overflow-hidden bg-black/50">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="px-3 py-1 bg-green-600/90 text-white rounded-full text-xs font-mono flex items-center gap-1 backdrop-blur-sm">
                      <Tag className="w-3 h-3" />
                      -{calculateDiscount(product.originalPrice, product.discountPrice)}%
                    </span>
                  </div>
                </div>
                <CardFooter className="p-4">
                  <Button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBuyProduct(product);
                    }}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full font-mono text-sm tracking-wider uppercase flex items-center justify-center gap-2 py-5"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Comprar
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Purchase Modal */}
      <PurchaseModal 
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default OutletSection;
